import {
    STATUS_QUERY,
    buildBitmapFrame,
    chunkBytes,
    decodeStatusByte,
    packImage,
    parsePrintedCount
} from './bitmap.js';
import { isValidZpl, renderZpl, PRINTER_DPI } from './raster.js';

const SERVICE_UUID = '49535343-fe7d-4ae5-8fa9-9fafd205e455';
const WRITE_UUID = '49535343-8841-43f4-a8d4-ecbe34729bb3';
const NOTIFY_UUID = '49535343-1e4d-4bd9-ba61-23c647249616';
const CONNECT_TIMEOUT_MS = 10000;
const STATUS_TIMEOUT_MS = 5000;
const PRINT_TIMEOUT_MS = 20000;
const SCAN_SECONDS = 10;
const ANDROID_MTU = 517;
const IOS_CHUNK_SIZE = 182;
const FALLBACK_CHUNK_SIZE = 20;

/**
 * @param {string} reason
 * @returns {Error}
 */
function fail(reason) {
    const error = new Error(reason);
    error.reason = reason;
    return error;
}

/**
 * @param {unknown} error
 * @returns {string}
 */
function reasonOf(error) {
    if (error && error.reason) {
        return error.reason;
    }
    const text = String(error && error.message ? error.message : error).toLowerCase();
    if (text.indexOf('bluetooth') !== -1) {
        return 'bluetooth_off';
    }
    if (text.indexOf('disconnect') !== -1 || text.indexOf('connection') !== -1) {
        return 'connection_lost';
    }
    return 'out_of_range';
}

/**
 * @param {unknown} data
 * @returns {Uint8Array}
 */
function asBytes(data) {
    if (data instanceof ArrayBuffer) {
        return new Uint8Array(data);
    }
    if (ArrayBuffer.isView(data)) {
        return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    }
    if (Array.isArray(data) && data.length > 0) {
        return asBytes(data[0]);
    }
    return new Uint8Array(0);
}

/**
 * Copia el fragmento: el plugin retiene el ArrayBuffer entre escrituras.
 *
 * @param {Uint8Array} bytes
 * @returns {ArrayBuffer}
 */
function copyBuffer(bytes) {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
}

/**
 * Impresora GPrinter por BLE. Cada llamada abre la conexión y la cierra.
 */
class GPrinter {
    /**
     * @param {object} [bleAdapter]
     * @param {{ createCanvas?: function, platform?: string }} [options]
     */
    constructor(bleAdapter, options) {
        const settings = options || {};
        this.ble = bleAdapter || (typeof ble !== 'undefined' ? ble : null);
        this.createCanvas = settings.createCanvas;
        this.platform = settings.platform || '';
        this.printTimeoutMs = settings.printTimeoutMs > 0 ? settings.printTimeoutMs : PRINT_TIMEOUT_MS;
        this.onNotify = null;
    }

    /**
     * @param {string} mac
     * @returns {Promise<{ available: boolean, mac: string, reason: string|null }>}
     */
    async getStatus(mac) {
        const address = typeof mac === 'string' ? mac.trim() : '';
        log('[GPrinter] getStatus', address || '(sin identificador)');
        if (address === '') {
            log('[GPrinter] getStatus resultado', 'out_of_range');
            return { available: false, mac: address, reason: 'out_of_range' };
        }

        let deviceId = address;
        let subscribed = false;
        try {
            deviceId = await this.open(address);
            subscribed = true;
            const byte = await this.queryStatus(deviceId);
            const decoded = decodeStatusByte(byte);
            log('[GPrinter] getStatus resultado', { available: decoded.available, reason: decoded.reason, byte: byte });
            return { available: decoded.available, mac: address, reason: decoded.reason };
        } catch (error) {
            log('[GPrinter] getStatus error', reasonOf(error), error && error.message);
            return { available: false, mac: address, reason: reasonOf(error) };
        } finally {
            await this.release(deviceId, subscribed);
        }
    }

    /**
     * @param {{ mac: string, zpl: string, dpi?: number }} params
     * @returns {Promise<{ mac: string, copies: number }>}
     */
    async print(params) {
        const address = params && typeof params.mac === 'string' ? params.mac.trim() : '';
        const zpl = params ? params.zpl : '';
        const dpi = params && params.dpi > 0 ? params.dpi : PRINTER_DPI;

        if (address === '') {
            throw fail('missing_mac');
        }
        if (!isValidZpl(zpl)) {
            throw fail('invalid_zpl');
        }

        const rendered = renderZpl(zpl, { dpi: dpi, createCanvas: this.createCanvas });
        const frame = this.frameFromCanvas(rendered);
        let deviceId = address;
        let subscribed = false;

        try {
            deviceId = await this.open(address);
            subscribed = true;
            const chunkSize = await this.chunkSize(deviceId);
            const confirmation = this.waitForPrint();
            await this.writeFrame(deviceId, frame, chunkSize);
            confirmation.arm();
            const copies = await confirmation.done;
            return { mac: address, copies: copies };
        } catch (error) {
            throw await this.reasonAfterTimeout(deviceId, subscribed, error);
        } finally {
            await this.release(deviceId, subscribed);
        }
    }

    /**
     * @param {string} address
     * @returns {Promise<string>}
     */
    async open(address) {
        await this.ensureBluetooth();
        const deviceId = await this.connect(address);
        await this.subscribe(deviceId);
        return deviceId;
    }

    /**
     * @returns {Promise<void>}
     */
    ensureBluetooth() {
        if (!this.ble) {
            return Promise.reject(fail('bluetooth_off'));
        }
        return this.call('isEnabled').catch(() => this.enableBluetooth());
    }

    /**
     * @returns {Promise<void>}
     */
    enableBluetooth() {
        if (!this.isAndroid() || typeof this.ble.enable !== 'function') {
            return Promise.reject(fail('bluetooth_off'));
        }
        return this.call('enable').catch(() => Promise.reject(fail('bluetooth_off')));
    }

    /**
     * @returns {boolean}
     */
    isAndroid() {
        if (this.platform === 'android') {
            return true;
        }
        return typeof cordova !== 'undefined' && cordova.platformId === 'android';
    }

    /**
     * @param {string} mac
     * @returns {Promise<string>}
     */
    async connect(mac) {
        log('[GPrinter] connect', mac);
        try {
            await this.connectId(mac);
            log('[GPrinter] connect ok', mac);
            return mac;
        } catch (error) {
            if (error && error.reason === 'bluetooth_off') {
                log('[GPrinter] bluetooth apagado');
                throw error;
            }
            log('[GPrinter] connect falló, busco el id', error && error.reason);
            const found = await this.findById(mac);
            if (!found) {
                log('[GPrinter] no encontré el id', mac);
                throw fail('out_of_range');
            }
            log('[GPrinter] reintento con', found);
            await this.connectId(found);
            return found;
        }
    }

    /**
     * @param {string} id
     * @returns {Promise<void>}
     */
    connectId(id) {
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                reject(fail('out_of_range'));
            }, CONNECT_TIMEOUT_MS);
            this.ble.connect(
                id,
                () => {
                    clearTimeout(timer);
                    resolve();
                },
                () => {
                    clearTimeout(timer);
                    reject(fail('out_of_range'));
                }
            );
        });
    }

    /**
     * @param {string} mac
     * @returns {Promise<string|null>}
     */
    findById(mac) {
        const wanted = mac.toLowerCase();
        return new Promise((resolve) => {
            let settled = false;
            const finish = (id) => {
                if (settled) {
                    return;
                }
                settled = true;
                clearTimeout(timer);
                const done = () => resolve(id);
                if (typeof this.ble.stopScan !== 'function') {
                    done();
                    return;
                }
                this.ble.stopScan(done, done);
            };
            const timer = setTimeout(() => finish(null), (SCAN_SECONDS * 1000) + 500);

            this.ble.scan(
                [],
                SCAN_SECONDS,
                (peripheral) => {
                    if (peripheral && String(peripheral.id).toLowerCase() === wanted) {
                        finish(peripheral.id);
                    }
                },
                () => finish(null)
            );
        });
    }

    /**
     * @param {string} deviceId
     * @returns {Promise<void>}
     */
    subscribe(deviceId) {
        return new Promise((resolve, reject) => {
            let registered = false;
            const timer = setTimeout(() => {
                reject(fail('no_response'));
            }, CONNECT_TIMEOUT_MS);

            this.ble.startNotification(
                deviceId,
                SERVICE_UUID,
                NOTIFY_UUID,
                (data) => {
                    if (data === 'registered') {
                        if (!registered) {
                            registered = true;
                            clearTimeout(timer);
                            resolve();
                        }
                        return;
                    }
                    if (this.onNotify) {
                        this.onNotify(data);
                    }
                },
                () => {
                    clearTimeout(timer);
                    reject(fail('connection_lost'));
                },
                { emitOnRegistered: true }
            );
        });
    }

    /**
     * @param {string} deviceId
     * @returns {Promise<number>}
     */
    queryStatus(deviceId) {
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                this.onNotify = null;
                reject(fail('no_response'));
            }, STATUS_TIMEOUT_MS);

            this.onNotify = (data) => {
                const bytes = asBytes(data);
                if (bytes.length === 0) {
                    return;
                }
                clearTimeout(timer);
                this.onNotify = null;
                resolve(bytes[0]);
            };

            this.writeChunk(deviceId, STATUS_QUERY).catch((error) => {
                clearTimeout(timer);
                this.onNotify = null;
                reject(error);
            });
        });
    }

    /**
     * @param {{ canvas: { getContext: function }, widthDots: number, heightDots: number, copies: number }} rendered
     * @returns {Uint8Array}
     */
    frameFromCanvas(rendered) {
        const context = rendered.canvas.getContext('2d');
        const image = context.getImageData(0, 0, rendered.widthDots, rendered.heightDots);
        const bitmap = packImage(image.data, rendered.widthDots, rendered.heightDots);
        return buildBitmapFrame({
            widthDots: rendered.widthDots,
            heightDots: rendered.heightDots,
            bitmap: bitmap,
            copies: rendered.copies
        });
    }

    /**
     * @param {string} deviceId
     * @returns {Promise<number>}
     */
    chunkSize(deviceId) {
        if (!this.isAndroid() || typeof this.ble.requestMtu !== 'function') {
            return Promise.resolve(IOS_CHUNK_SIZE);
        }
        return this.call('requestMtu', [deviceId, ANDROID_MTU])
            .then((mtu) => {
                const granted = typeof mtu === 'number' ? mtu : ANDROID_MTU;
                return Math.max(granted - 3, 1);
            })
            .catch(() => FALLBACK_CHUNK_SIZE);
    }

    /**
     * @returns {{ done: Promise<number>, arm: function }}
     */
    waitForPrint() {
        let settled = false;
        let timer = null;
        let resolveWait;
        let rejectWait;
        const done = new Promise((resolve, reject) => {
            resolveWait = resolve;
            rejectWait = reject;
        });

        this.onNotify = (data) => {
            const count = parsePrintedCount(asBytes(data));
            if (!Number.isFinite(count) || settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            this.onNotify = null;
            resolveWait(count);
        };

        return {
            done: done,
            arm: () => {
                if (settled) {
                    return;
                }
                timer = setTimeout(() => {
                    if (settled) {
                        return;
                    }
                    settled = true;
                    this.onNotify = null;
                    rejectWait(fail('no_response'));
                }, this.printTimeoutMs);
            }
        };
    }

    /**
     * Con la tapa abierta la impresora no avisa de fin. <ESC>!? sigue contestando.
     *
     * @param {string} deviceId
     * @param {boolean} subscribed
     * @param {Error} error
     * @returns {Promise<Error>}
     */
    async reasonAfterTimeout(deviceId, subscribed, error) {
        if (!subscribed || !error || error.reason !== 'no_response') {
            return error;
        }
        try {
            const byte = await this.queryStatus(deviceId);
            const decoded = decodeStatusByte(byte);
            if (decoded.reason === 'head_opened' || decoded.reason === 'cover_opened' || decoded.reason === 'out_of_paper') {
                return fail(decoded.reason);
            }
        } catch (statusError) {
            return error;
        }
        return error;
    }

    /**
     * @param {string} deviceId
     * @param {Uint8Array} frame
     * @param {number} size
     * @returns {Promise<void>}
     */
    async writeFrame(deviceId, frame, size) {
        const chunks = chunkBytes(frame, size);
        for (let index = 0; index < chunks.length; index += 1) {
            await this.writeChunk(deviceId, chunks[index]);
        }
    }

    /**
     * @param {string} deviceId
     * @param {Uint8Array} bytes
     * @returns {Promise<void>}
     */
    writeChunk(deviceId, bytes) {
        const payload = copyBuffer(bytes);
        return this.call('write', [deviceId, SERVICE_UUID, WRITE_UUID, payload]).catch(() => {
            return this.call('write', [deviceId, SERVICE_UUID, WRITE_UUID, copyBuffer(bytes)]).catch(() => {
                return Promise.reject(fail('connection_lost'));
            });
        });
    }

    /**
     * @param {string} deviceId
     * @param {boolean} subscribed
     * @returns {Promise<void>}
     */
    async release(deviceId, subscribed) {
        if (!this.ble || !deviceId) {
            return;
        }
        this.onNotify = null;
        if (subscribed) {
            await this.call('stopNotification', [deviceId, SERVICE_UUID, NOTIFY_UUID]).catch(() => undefined);
        }
        await this.call('disconnect', [deviceId]).catch(() => undefined);
    }

    /**
     * @param {string} method
     * @param {unknown[]} [args]
     * @returns {Promise<unknown>}
     */
    call(method, args) {
        const params = args ? args.slice() : [];
        return new Promise((resolve, reject) => {
            params.push(resolve);
            params.push(reject);
            this.ble[method].apply(this.ble, params);
        });
    }
}

export default GPrinter;
